/**
 * ================ API error handling utilities ================
 *
 * This module exports helpers that can be used to check if API
 * response errors are some specific error cases.
 *
 * NOTE: most of the functions are tied to an endpoint, and should not
 * be used to check error responses from any other endpoint. Check the
 * name and the docstring of the function to ensure correct usage.
 */

import {
  ERROR_CODE_ANNUAL_PASS_VOUCHER_RESTRICTION,
  ERROR_CODE_BOOKING_PENDING_RESCUE,
  ERROR_CODE_CHARGE_DISABLED,
  ERROR_CODE_CHARGE_ZERO_PAYIN,
  ERROR_CODE_EMAIL_NOT_FOUND,
  ERROR_CODE_EMAIL_TAKEN,
  ERROR_CODE_INVALID_PHONE_NUMBER,
  ERROR_CODE_MISSING_STRIPE_ACCOUNT,
  ERROR_CODE_PAYMENT_FAILED,
  ERROR_CODE_PHONE_NUMBER_BLOCKED,
  ERROR_CODE_TOO_MANY_VERIFICATION_REQUESTS,
  ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_CUSTOMER,
  ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_PROVIDER,
  ERROR_CODE_TRANSACTION_AUTHORIZED_PENDING_RESCUE,
  ERROR_CODE_TRANSACTION_BOOKING_TIME_NOT_AVAILABLE,
  ERROR_CODE_TRANSACTION_INVALID_TRANSITION,
  ERROR_CODE_TRANSACTION_LISTING_NOT_FOUND,
  ERROR_CODE_UPLOAD_OVER_LIMIT,
  ERROR_CODE_VOUCHERIFY,
} from './types';

const errorAPIErrors = (error) => {
  return error && error.apiErrors ? error.apiErrors : [];
};

const hasErrorWithCode = (error, code) => {
  return errorAPIErrors(error).some((apiError) => {
    return apiError.code === code;
  });
};

/**
 * return apiErrors from error response
 * @param {object} error - The error object.
 * @returns {object[]} - The api errors.
 */
const responseAPIErrors = (error) => {
  return error && error.data && error.data.errors ? error.data.errors : [];
};

/**
 * Check if the given API error (from `sdk.currentuser.create()`) is
 * due to the email address already being in use.
 * @param {object} error - The error object.
 * @returns {boolean} - True if the email address is already in use, false otherwise.
 */
export const isSignupEmailTakenError = (error) => hasErrorWithCode(error, ERROR_CODE_EMAIL_TAKEN);

/**
 * Check if the given API error (from `sdk.currentuser.changeEmail()`) is
 * due to the email address already being in use.
 * @param {object} error - The error object.
 * @returns {boolean} - True if the email address is already in use, false otherwise.
 */
export const isChangeEmailTakenError = (error) => hasErrorWithCode(error, ERROR_CODE_EMAIL_TAKEN);

/**
 * Check if the given API error (from
 * `sdk.currentUser.sendVerificationEmail()`) is due to too many
 * active email verification requests.
 *
 * There qre only a specific amount of active verification requests
 * allowed, and the user has to wait for them to expire to be able to
 * request sending new verification emails.
 * @param {object} error - The error object.
 * @returns {boolean} - True if the email address is already in use, false otherwise.
 */
export const isTooManyEmailVerificationRequestsError = (error) =>
  hasErrorWithCode(error, ERROR_CODE_TOO_MANY_VERIFICATION_REQUESTS);

/**
 * Check if the given error (from `createPhoneNumberVerificationCode()`) is due
 * to Twilio Lookup not accepting the phone number.
 * @param {object} error - The stored error.
 * @returns {boolean} - True if the phone number is invalid, false otherwise.
 */
export const isInvalidPhoneNumberError = (error) => error?.code === ERROR_CODE_INVALID_PHONE_NUMBER;

/**
 * Check if the given error (from `createPhoneNumberVerificationCode()`) is due
 * to Twilio refusing to deliver a code to the phone number.
 * @param {object} error - The stored error.
 * @returns {boolean} - True if delivery to the phone number is blocked, false otherwise.
 */
export const isPhoneNumberBlockedError = (error) => error?.code === ERROR_CODE_PHONE_NUMBER_BLOCKED;

/**
 * Check if the given error from our own API is a rate-limit rejection
 * (HTTP 429): an express-rate-limit limiter in server/apiRouter.js, or a
 * Twilio 429 such as "max send attempts reached" passed on by handleError.
 * @param {object} error - The error object.
 * @returns {boolean} - True if the request was rate limited, false otherwise.
 */
export const isTooManyRequestsError = (error) => error?.status === 429;

/**
 * Check if the given API error (from
 * `sdk.images.upload()`) is due to the image being over
 * the size limit.
 * @param {object} error - The error object.
 * @returns {boolean} - True if the image is over the size limit, false otherwise.
 */
export const isUploadImageOverLimitError = (error) =>
  hasErrorWithCode(error, ERROR_CODE_UPLOAD_OVER_LIMIT);

/**
 * Check if the given API error (from `sdk.passwordReset.request()`)
 * is due to no user having the given email address.
 * @param {object} error - The error object.
 * @returns {boolean} - True if the email address is not found, false otherwise.
 */
export const isPasswordRecoveryEmailNotFoundError = (error) =>
  hasErrorWithCode(error, ERROR_CODE_EMAIL_NOT_FOUND);

/**
 * Check if the given API error (from `sdk.transaction.initiate()` or
 * `sdk.transaction.initiateSpeculative()`) is due to the listing
 * being closed or deleted.
 * @param {object} error - The error object.
 * @returns {boolean} - True if the listing is not found, false otherwise.
 */
export const isTransactionInitiateListingNotFoundError = (error) =>
  hasErrorWithCode(error, ERROR_CODE_TRANSACTION_LISTING_NOT_FOUND);

/**
 * Check if the given API error (from `sdk.transaction.initiate()` or
 * `sdk.transaction.initiateSpeculative()`) is due to missign Stripe
 * connection from the listing author.
 * @param {object} error - The error object.
 * @returns {boolean} - True if the Stripe account is missing, false otherwise.
 */
export const isTransactionInitiateMissingStripeAccountError = (error) =>
  hasErrorWithCode(error, ERROR_CODE_MISSING_STRIPE_ACCOUNT);

/**
 *  Check if the given API error (from `sdk.transaction.initiate()` or
 *  `sdk.transaction.initiateSpeculative()`) is due to annual pass voucher restriction.
 *  This error is thrown when the user tries to use an annual pass voucher for a booking
 * @param {object} error - The error object.
 * @returns {boolean} - True if the annual pass voucher is restricted, false otherwise.
 */

export const isTransactionAnnualPassRestrictionError = (error) =>
  hasErrorWithCode(error, ERROR_CODE_ANNUAL_PASS_VOUCHER_RESTRICTION);

/**
 * Check if the given API error (from `sdk.transaction.initiate()` or
 * `sdk.transaction.initiateSpeculative()`) is due to selected booking
 * time already being booked.
 * @param {object} error - The error object.
 * @returns {boolean} - True if the booking time is not available, false otherwise.
 */
export const isTransactionInitiateBookingTimeNotAvailableError = (error) =>
  hasErrorWithCode(error, ERROR_CODE_TRANSACTION_BOOKING_TIME_NOT_AVAILABLE);

/**
 * Check if the given API error (from `sdk.transaction.initiate()` or
 * `sdk.transaction.initiateSpeculative()`) is due to payment being zero.
 * @param {object} error - The error object.
 * @returns {boolean} - True if the payment is zero, false otherwise.
 */
export const isTransactionZeroPaymentError = (error) =>
  hasErrorWithCode(error, ERROR_CODE_CHARGE_ZERO_PAYIN);

/**
 * Check if the given API error (from `sdk.transaction.initiate()` or
 * `sdk.transaction.initiateSpeculative()`) is due to voucherify error.
 * @param {object} error - The error object.
 * @returns {boolean} - True if the voucherify error, false otherwise.
 */
export const isVoucherifyError = (error) => error?.data?.errorCode === ERROR_CODE_VOUCHERIFY;

// Beide Codes bedeuten dasselbe: die Zahlung ist autorisiert, der Server bringt
// die Transaktion selbst zu Ende. Der Checkout darf daraufhin keinen Fehler
// zeigen, sondern muss auf das Ergebnis warten.
const PENDING_RESCUE_CODES = [
  ERROR_CODE_TRANSACTION_AUTHORIZED_PENDING_RESCUE,
  ERROR_CODE_BOOKING_PENDING_RESCUE,
];

/**
 * Transaktion, auf deren serverseitige Rettung der Checkout warten soll.
 *
 * Die Fehler kommen aus der eigenen API (`abort-unpaid-transaction` bzw.
 * `initiate-privileged`) und werden von `handleError` (server/api-util/sdk.js)
 * so serialisiert, dass die strukturierten Felder unter `data` landen — genau
 * die Felder, die `storableError` behält. Deshalb NICHT auf `error.code`
 * prüfen: handleError legt den Code unter `data` (und als `errorCode`) ab,
 * nie als `code` auf oberster Ebene.
 *
 * @param {object} error Fehler aus util/api.js oder storableError().
 * @returns {string|null} UUID der zu beobachtenden Transaktion, sonst null.
 */
export const pendingRescueTransactionId = (error) => {
  if (error?.status !== 409) return null;
  if (!PENDING_RESCUE_CODES.includes(error?.data?.code)) return null;
  return error.data.transactionId || null;
};

/**
 * Check if the given API error (from `sdk.transaction.initiate()`) is
 * due to the transaction total amount being too low for Stripe.
 * @param {object} error - The error object.
 * @returns {boolean} - True if the transaction total amount is too low, false otherwise.
 */
export const isTransactionInitiateAmountTooLowError = (error) => {
  const isZeroPayment = isTransactionZeroPaymentError(error);

  const tooLowAmount = errorAPIErrors(error).some((apiError) => {
    const isPaymentFailedError =
      apiError.status === 402 && apiError.code === ERROR_CODE_PAYMENT_FAILED;
    let isAmountTooLow = false;

    try {
      // TODO: This is a temporary solution until a proper error code
      // for this specific error is received in the response.
      const msg = apiError.meta.stripeMessage;
      isAmountTooLow =
        msg.startsWith('Amount must be at least') ||
        msg.startsWith('Amount must convert to at least');
    } catch (e) {
      // Ignore
    }

    return isPaymentFailedError && isAmountTooLow;
  });

  return isZeroPayment || tooLowAmount;
};

/**
 * Check if the given API error (from `sdk.transaction.initiate()`) is
 * due to the transaction charge creation disabled by Stripe.
 * @param {object} error - The error object.
 * @returns {boolean} - True if the transaction charge creation is disabled, false otherwise.
 */
export const isTransactionChargeDisabledError = (error) => {
  // Eigener Guard in initiate-privileged: der Connect-Account des Gastgebers
  // kann keine Überweisung annehmen. Kommt als sauberer Code, nicht als
  // Stripe-Meldung im Freitext.
  if (hasErrorWithCode(error, ERROR_CODE_CHARGE_DISABLED)) {
    return true;
  }

  const chargeCreationDisabled = errorAPIErrors(error).some((apiError) => {
    const isPaymentFailedError =
      apiError.status === 402 && apiError.code === ERROR_CODE_PAYMENT_FAILED;

    let isChargeCreationDisabled = false;
    try {
      const msg = apiError.meta.stripeMessage;
      isChargeCreationDisabled =
        msg.startsWith('Your account cannot currently make charges.') ||
        msg.match(/verification.disabled_reason/);
    } catch (e) {
      // Ignore
    }

    return isPaymentFailedError && isChargeCreationDisabled;
  });

  return chargeCreationDisabled;
};

/**
 * Check if the given API error (from `sdk.transaction.initiate()`) is
 * due to other error in Stripe.
 * @param {object} error - The error object.
 * @returns {string[] | null} - The Stripe error messages.
 */
export const transactionInitiateOrderStripeErrors = (error) => {
  if (error) {
    return errorAPIErrors(error).reduce((messages, apiError) => {
      const isPaymentFailedError =
        apiError.status === 402 && apiError.code === ERROR_CODE_PAYMENT_FAILED;
      const hasStripeError = apiError && apiError.meta && apiError.meta.stripeMessage;
      const stripeMessageMaybe =
        isPaymentFailedError && hasStripeError ? [apiError.meta.stripeMessage] : [];
      return [...messages, ...stripeMessageMaybe];
    }, []);
  }
  return null;
};

/**
 * Check if the given API error (from `sdk.transactions.transition(id, transition, params)`)
 * is due to invalid transition attempt.
 * @param {object} error - The error object.
 * @returns {boolean} - True if the transition is invalid, false otherwise.
 */
export const isTransactionsTransitionInvalidTransition = (error) =>
  error &&
  error.status === 409 &&
  hasErrorWithCode(error, ERROR_CODE_TRANSACTION_INVALID_TRANSITION);

/**
 * Check if the given API error (from `sdk.transactions.transition(id, transition, params)`)
 * is due to already sent review.
 * @param {object} error - The error object.
 * @returns {boolean} - True if the transaction is already reviewed, false otherwise.
 */
export const isTransactionsTransitionAlreadyReviewed = (error) =>
  error &&
  error.status === 409 &&
  (hasErrorWithCode(error, ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_CUSTOMER) ||
    hasErrorWithCode(error, ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_PROVIDER));

/**
 * Check if the given API error (from `sdk.currentUser.changeEmail(params)`)
 * is due to giving wrong password.
 * @param {object} error - The error object.
 * @returns {boolean} - True if the password is wrong, false otherwise.
 */
export const isChangeEmailWrongPassword = (error) => error && error.status === 403;

/**
 * Check if the given API error (from `sdk.currentUser.changePassword(params)`)
 * is due to giving wrong password.
 * @param {object} error - The error object.
 * @returns {boolean} - True if the password is wrong, false otherwise.
 */
export const isChangePasswordWrongPassword = (error) => error && error.status === 403;

/**
 * Check if the given API error (from
 * 'sdk.stripeAccount.create(payoutDetails)') is due to
 * invalid postal code in the given country.
 * @param {object} error - The error object.
 * @returns {boolean} - True if the postal code is invalid, false otherwise.
 */
export const isStripeInvalidPostalCode = (error) => {
  const msgRe = /^Invalid [A-Z]{2} postal code$/;
  return errorAPIErrors(error).some((apiError) => {
    // Stripe doesn't seem to give an error code for this specific
    // case, so we have to recognize it from the message.
    const msg = apiError.meta && apiError.meta.stripeMessage ? apiError.meta.stripeMessage : '';
    return msgRe.test(msg);
  });
};

export const isStripeError = (error) => {
  return errorAPIErrors(error).some((apiError) => {
    // Stripe doesn't seem to give an error code for this specific
    // case, so we have to recognize it from the message.
    return !!(apiError.meta && apiError.meta.stripeMessage);
  });
};

export const storableError = (err) => {
  const error = err || {};
  const { name, message, status, statusText, title, detail, data, code } = error;
  // Status, statusText, and data.errors are (possibly) added to the error object by SDK
  const apiErrors = responseAPIErrors(error);

  // Returned object is the same as prop type check in util/types -> error
  return {
    type: 'error',
    name: name || title,
    message: message || detail,
    status,
    statusText,
    apiErrors,
    data,
    // A top-level `code` of our own API's error body (util/api.js copies the
    // body onto the Error), e.g. 'invalid-phone-number' or the rate limiter's
    // 'too-many-requests'. Only set when present, so every other error keeps
    // its exact shape.
    ...(code !== undefined ? { code } : {}),
  };
};

export const responseApiErrorInfo = (err) =>
  responseAPIErrors(err).map((e) => ({
    status: e.status,
    code: e.code,
    meta: e.meta,
  }));
